/** Targets and plan / Recommendations - EmitIQ's analysis from your data (ADR-0212 decision 7).
 *
 * A table page: a title card, three tiles, one filter line and one gridded table. **Everything on it
 * is the server's** (`GET /plan/recommendations`): the rows, their order, the tonnes and the share are
 * read and printed, never recomputed, so the tab cannot disagree with the inventory about what the
 * largest source is. The tiles are the server's strings; the table is sorted by the server's rank
 * unless the reader sorts a column.
 *
 * What a row never says: a saving, a cost, a deadline or anything about the law. Each row is a place
 * to look, the figure it rests on (a receipt, opened in the usual drawer, where a figure exists) and
 * the link that fixes it. A row about something missing carries a count and no receipt.
 */

import { Leaf } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { useMe } from "../api/auth";
import { useRecommendations } from "../api/recommendations";
import type { Recommendation } from "../api/recommendations";
import { ReadFailed } from "../components/Loaded";
import { EmptyState } from "../components/ui/EmptyState";
import { FilterLine } from "../components/ui/FilterLine";
import { EMPTY_FILTERS } from "../components/ui/filters";
import type { FilterDef, FilterState } from "../components/ui/filters";
import { GridTable } from "../components/ui/GridTable";
import type { GridColumn } from "../components/ui/GridTable";
import { HowDrawer } from "../components/ui/HowDrawer";
import { StatusChip } from "../components/ui/StatusChip";
import { TablePage } from "../components/ui/TablePage";
import { DashTiles, Tile, TileStrip } from "../components/ui/Tile";
import { quantity, share } from "../lib/format";
import { mayEnterData } from "../shell/routeTable";
import { useGridSort } from "./gridSort";
import { NoPeriod } from "./NoPeriod";
import type { ScreenSearch } from "./search";
import { keepSearch } from "./search";
import styles from "./Screen.module.css";
import type { Screen } from "./useScreen";

/** The kinds, in the words a reader filters by. The server's `kind` is the key. */
const KIND_LABEL: Readonly<Record<string, string>> = {
  largest_source: "Largest source",
  estimates_to_replace: "Estimates",
  site_gap: "Missing bills",
  awaiting_acceptance: "Awaiting a second person",
  categories_not_assessed: "Scope 3 categories",
};

const KIND_FILTER: FilterDef = {
  id: "kind",
  label: "Kind",
  mode: "single",
  allLabel: "All kinds",
  options: Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label })),
};

/** Where a fix action goes. The closed list is the server's (`recommendations.py`); an action this
 * file does not know is shown as no link rather than guessed at. */
function FixLink({ row }: { row: Recommendation }) {
  const { data: me } = useMe();
  const common = (prev: Partial<ScreenSearch>) => keepSearch(prev);
  const label = row.fix.label;
  // **A link a role cannot follow is not drawn** (Q-05, G-Wviewer-19). The server's row is the same for
  // everyone, so the screen decides: a Viewer is not sent to Upload, and Processing is the upload roles'
  // place, so the gaps open as the standard report of the same gaps, as Home's Missing tile does.
  const enters = mayEnterData(me?.role);
  switch (row.fix.action) {
    case "open_record": {
      const id = row.fix.target.record_id;
      if (!id) return <span className={styles.note}>–</span>;
      return (
        <Link to="/records/$recordId" params={{ recordId: id }} search={common} data-testid="recommendation-fix">
          {label}
        </Link>
      );
    }
    case "upload_document":
      if (!enters) return <span className={styles.note}>Needs an uploader</span>;
      return (
        <Link to="/upload" search={common} data-testid="recommendation-fix">
          {label}
        </Link>
      );
    case "open_site_gaps":
      return enters ? (
        <Link
          to="/processing"
          search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab: "missing" })}
          data-testid="recommendation-fix"
        >
          {label}
        </Link>
      ) : (
        <Link
          to="/data-reports"
          search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), report: "missing-data" })}
          data-testid="recommendation-fix"
        >
          {label}
        </Link>
      );
    case "open_action_items":
      return (
        <Link to="/approvals" search={common} data-testid="recommendation-fix">
          {label}
        </Link>
      );
    case "decide_scope3_categories":
      return (
        <Link
          to="/scope-3"
          search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab: "categories" })}
          data-testid="recommendation-fix"
        >
          {label}
        </Link>
      );
    default:
      return <span className={styles.note}>–</span>;
  }
}

/** What the row rests on: the label, the tonnes where there are any, and the receipt where a figure
 * exists. A row with no figure prints its count and nothing that looks like a measurement. */
function BasedOn({
  row,
  total,
  onReceipt,
}: {
  row: Recommendation;
  total: string;
  onReceipt: (calculationId: string) => void;
}) {
  const basis = row.basis;
  const part = basis.share !== null && basis.tco2e !== null ? share(basis.tco2e, total) : null;
  const text =
    basis.tco2e !== null
      ? `${basis.label}: ${quantity(basis.tco2e, 1)} tCO2e${part ? `, ${part}` : ""}`
      : basis.label;
  return (
    <span data-testid="recommendation-basis">
      {basis.calculation_id ? (
        <button
          type="button"
          className={styles.fixLink}
          data-testid="recommendation-receipt"
          aria-label={`Receipt for ${row.subject}: ${text}`}
          onClick={() => onReceipt(basis.calculation_id as string)}
        >
          {text}
        </button>
      ) : (
        text
      )}
    </span>
  );
}

export function RecommendationsTab({ screen }: { screen: Screen }) {
  const window = screen.window;
  const read = useRecommendations(window?.period_start, window?.period_end, screen.method);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const data = read.data;
  const kind = filters.values.kind?.[0];
  const needle = filters.search.trim().toLowerCase();

  // The server's order is the default. A filter only removes rows; it never reorders them.
  const kept = (data?.items ?? []).filter(
    (row) =>
      (!kind || row.kind === kind) &&
      (!needle || `${row.subject} ${row.what_to_do}`.toLowerCase().includes(needle)),
  );
  const { sorted, sort, setSort } = useGridSort(kept, {
    recommendation: (row) => row.rank,
    based_on: (row) => (row.at_stake_tco2e === null ? -1 : Number(row.at_stake_tco2e)),
  });

  const columns: GridColumn<Recommendation>[] = [
    {
      id: "recommendation",
      header: "Recommendation",
      sortable: true,
      rowHeader: true,
      // A recommendation is a sentence about a site and what to do: it wraps and is not cut at "Bills expecte...".
      wrap: true,
      cell: (row) => (
        <span data-testid={`recommendation-${row.kind}`}>
          <strong>{row.subject}.</strong> {row.what_to_do}
        </span>
      ),
    },
    {
      id: "kind",
      header: "Kind",
      cell: (row) => <StatusChip tone="neutral" size="sm">{KIND_LABEL[row.kind] ?? row.kind}</StatusChip>,
    },
    {
      id: "based_on",
      header: "Based on",
      sortable: true,
      cell: (row) => <BasedOn row={row} total={data?.total_tco2e ?? "0"} onReceipt={screen.openReceipt} />,
    },
    { id: "link", header: "Link", cell: (row) => <FixLink row={row} /> },
  ];

  const header = (
    <>
      <StatusChip tone="info" size="sm" testId="recommendations-label">
        {data?.label ?? "EmitIQ's analysis from your data"}
      </StatusChip>
      <HowDrawer infoKey="targets.recommendations" label="How these are worked out" />
    </>
  );

  if (!window) {
    return (
      <TablePage
        icon={Leaf}
        title="Targets & plan"
        label="Recommendations"
        actions={header}
        tiles={
          <DashTiles
            labels={["Largest source share", "Estimates to replace", "Sites with a gap"]}
            label="Recommendations at a glance"
          />
        }
        state={
          <div data-testid="recommendations-no-period">
            <NoPeriod />
          </div>
        }
      />
    );
  }
  if (read.error) {
    return (
      <TablePage
        icon={Leaf}
        title="Targets & plan"
        label="Recommendations"
        actions={header}
        state={<ReadFailed of="The recommendations" error={read.error} onRetry={() => void read.refetch()} />}
      />
    );
  }

  return (
    <TablePage
      icon={Leaf}
      title="Targets & plan"
      label="Recommendations"
      actions={header}
      tiles={
        <TileStrip label="Recommendations at a glance">
          <Tile
            value={data?.largest_share != null ? (share(data.largest_share, "1") ?? "-") : "-"}
            label="Largest source share"
            testId="recommendations-largest-share"
            read={read}
          />
          <Tile
            value={data ? quantity(String(data.estimate_figures)) : "-"}
            label="Estimates to replace"
            testId="recommendations-estimates"
            read={read}
          />
          <Tile
            value={data ? quantity(String(data.sites_with_gap)) : "-"}
            label="Sites with a gap"
            testId="recommendations-gaps"
            read={read}
          />
        </TileStrip>
      }
      filters={
        <FilterLine
          filters={[KIND_FILTER]}
          state={filters}
          onChange={setFilters}
          searchLabel="Search recommendations"
          resultCount={data ? { shown: kept.length, total: data.items.length, noun: "recommendations" } : undefined}
        />
      }
      table={
        <GridTable
          caption="Recommendations from your own data"
          columns={columns}
          rows={sorted}
          rowKey={(row) => row.key}
          rowNumbers={false}
          loading={read.isPending}
          sort={sort}
          onSortChange={setSort}
          empty={<EmptyState title="Nothing to look at." compact testId="recommendations-empty" />}
          testId="recommendations"
        />
      }
    />
  );
}
